import { useState } from 'react';
import { DEFAULT_COMPOSITION_QUANTITY_STEP } from './CargoTypeComposition';

function NumeralRow({ id, label, value, onChange, unit = 'MT' }) {
  return (
    <div
      className='d-flex align-items-center border rounded bg-white'
      style={{ height: 36 }}
    >
      <span className='small text-muted ps-2 pe-1'>{label}</span>
      <input
        id={id}
        type='number'
        step='0.001'
        placeholder='0.000'
        aria-label={label}
        value={value}
        onChange={e =>
          onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
        className='form-control border-0 bg-transparent shadow-none px-1'
        style={{
          minWidth: 0,
          flex: '1 1 0%',
          height: '100%',
          MozAppearance: 'textfield',
          WebkitAppearance: 'textfield',
        }}
      />
      <span className='small text-muted pe-2 ps-1 flex-shrink-0'>{unit}</span>
    </div>
  );
}

function RefrigeratedGroup({ title, data, onChange, idPrefix }) {
  return (
    <div className='border rounded p-3 bg-white'>
      <div className='small fw-semibold text-dark mb-3'>{title}</div>
      <div className='d-flex gap-3'>
        <div className='flex-fill'>
          <NumeralRow
            id={`${idPrefix}Min`}
            label='Min'
            value={data.min}
            onChange={v => onChange({ ...data, min: v })}
          />
        </div>
        <div className='flex-fill'>
          <NumeralRow
            id={`${idPrefix}Max`}
            label='Max'
            value={data.max}
            onChange={v => onChange({ ...data, max: v })}
          />
        </div>
      </div>
    </div>
  );
}

// Frequency of the single-component LPG types; the mixed types keep one per
// cargo type in CargoTypeComposition.
function FrequencyRow({ id, value, onChange, disabled }) {
  return (
    <div className='d-flex align-items-center justify-content-between border rounded bg-white px-3 py-2 mb-2'>
      <label htmlFor={id} className='small text-muted mb-0'>
        Frequency
      </label>
      <input
        id={id}
        type='number'
        min={0}
        placeholder='e.g. 12'
        value={value ?? ''}
        onChange={
          disabled
            ? undefined
            : e =>
                onChange(
                  e.target.value === ''
                    ? null
                    : Math.max(0, Math.trunc(Number(e.target.value)))
                )
        }
        readOnly={disabled}
        className={`form-control form-control-sm text-center${
          disabled ? ' bg-light' : ''
        }`}
        style={{ width: 90, height: 36 }}
      />
    </div>
  );
}

// Interval between the total quantities offered for a delivery term; empty
// keeps the default.
function IntervalRow({ id, value, onChange, disabled }) {
  return (
    <div className='d-flex align-items-center justify-content-between border rounded bg-white px-3 py-2 mb-2'>
      <label
        htmlFor={id}
        className='small text-muted mb-0'
        title='Quantity interval: each delivery quantity option goes up by this amount'
      >
        Interval
      </label>
      <div className='d-flex align-items-center gap-1'>
        <input
          id={id}
          type='number'
          min={0}
          step='any'
          placeholder={String(DEFAULT_COMPOSITION_QUANTITY_STEP)}
          aria-label='Quantity interval'
          value={value ?? ''}
          onChange={
            disabled
              ? undefined
              : e =>
                  onChange(
                    e.target.value === ''
                      ? null
                      : Math.max(0, Number(e.target.value))
                  )
          }
          readOnly={disabled}
          className={`form-control form-control-sm text-end${
            disabled ? ' bg-light' : ''
          }`}
          style={{ width: 90, height: 36 }}
        />
        <span className='small text-muted'>MT</span>
      </div>
    </div>
  );
}

// Edits one product type's composition. `form` is that product type's entry
// (plus isProcurementCompleted); `idPrefix` keeps field ids unique when the
// page shows several product types.
export default function Composition({
  form,
  setForm,
  comptitle,
  idPrefix = '',
}) {
  const [compositionError, setCompositionError] = useState(null);
  const isLocked = form.isProcurementCompleted !== false;

  const butaneData = {
    min: form.compositionMin ?? '',
    max: form.compositionMax ?? '',
  };

  const handleButaneChange = data => {
    const min = data.min === '' || data.min == null ? null : Number(data.min);
    const max = data.max === '' || data.max == null ? null : Number(data.max);

    if (min != null && max != null && min > max) {
      setCompositionError('Min cannot be greater than Max');
    } else {
      setCompositionError(null);
    }

    setForm(prev => ({
      ...prev,
      compositionMin: data.min === '' ? null : data.min,
      compositionMax: data.max === '' ? null : data.max,
    }));
  };

  return (
    <div className='mb-3'>
      <FrequencyRow
        id={`${idPrefix}compositionFrequency`}
        value={form.frequency}
        onChange={value => setForm(prev => ({ ...prev, frequency: value }))}
        disabled={isLocked}
      />
      <IntervalRow
        id={`${idPrefix}compositionQuantityStep`}
        value={form.compositionQuantityStep}
        onChange={value =>
          setForm(prev => ({ ...prev, compositionQuantityStep: value }))
        }
        disabled={isLocked}
      />
      <RefrigeratedGroup
        idPrefix={`${idPrefix}composition`}
        title={comptitle}
        data={butaneData}
        onChange={handleButaneChange}
      />
      {compositionError && (
        <div className='text-danger d-flex align-items-center gap-1 mt-1'>
          <i className='ri-error-warning-line'></i>
          <span>{compositionError}</span>
        </div>
      )}
    </div>
  );
}
